/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useCallback, useState } from "react"

import { TTRDeviceFormData } from "../../../../../components/devices/ttr/types/providerTypes"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import {
    getTsIoMapper,
    snackbar,
} from "../../../../../utils/general/generalUtils"
import { getTypeTestPostBodyFromFormData } from "../../../../../utils/registerDevice/registerDeviceUtils"
import { postClientProtected } from "../../../clients/clients"
import {
    PublishAmendmentResponse,
    TNewAmendmentPostBody,
    TNewAmendmentPostBodyPartial,
    TPublishAmendmentResponse,
} from "../../../types/devices/ttr/publishDeviceTypes"
import { PUBLISH_AMENDMENT } from "../../../urls/private"

interface IUsePublishAmendment {
    publishAmendment: () => Promise<TPublishAmendmentResponse>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

/** Returns the POST body for submitting a device amendment */
const getNewAmendmentPostBody = (
    formData: TTRDeviceFormData,
    deviceId: string,
    deviceRef: string,
    attachmentUUID: string
): TNewAmendmentPostBody => {
    const newAmendmentPostBodyPartial: TNewAmendmentPostBodyPartial = {
        ManufacturerID: formData.coreDetails.manufacturer!,
        DeviceID: deviceId,
        DeviceRef: deviceRef,
        ModelNo: formData.coreDetails.model,
        DeviceCategoryID: formData.coreDetails.deviceCategory!,
        DeviceTypeID: formData.coreDetails.deviceType!,
        RegisteredCapacity: formData.coreDetails.registeredCapacity! as number,
        SoftwareVersion: formData.coreDetails.softwareVersion,
        HardwareVersion: formData.coreDetails.hardwareVersion,
        DeviceComments: formData.coreDetails.additionalComments,
        PhaseID: formData.coreDetails.numberOfPhases!,
        AttachmentUUID: attachmentUUID,
    }

    const typeTestPostBody = getTypeTestPostBodyFromFormData(formData)

    return {
        ...newAmendmentPostBodyPartial,
        ...typeTestPostBody,
    }
}

const publishAmendmentMapper = getTsIoMapper(
    PublishAmendmentResponse,
    "publishAmendmentMapper"
)

/** Hook for publishing a device amendment, returns function for publishing amendments, loading and error states */
const usePublishAmendment = (
    formData: TTRDeviceFormData,
    attachmentUUID?: string,
    deviceId?: string,
    deviceRef?: string
): IUsePublishAmendment => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    const publishAmendment =
        useCallback(async (): Promise<TPublishAmendmentResponse> => {
            let response: TPublishAmendmentResponse = {
                published_device_id: "",
                amendment_ref: "",
            }

            if (!deviceId || !deviceRef || !attachmentUUID) {
                return response
            }

            const postBody = getNewAmendmentPostBody(
                formData,
                deviceId,
                deviceRef,
                attachmentUUID
            )

            try {
                setIsLoading(true)
                const postResponse = await postClientProtected(
                    PUBLISH_AMENDMENT,
                    postBody
                )
                response = publishAmendmentMapper.map(postResponse)
                snackbar("Successfully amended device", "success")
                setIsLoaded(true)
            } catch {
                const appError = invalidApiDataError("Couldn't amend Device")
                setError(appError)
                snackbar(
                    `Couldn't amend device, please try again. ${SUPPORT_REFER}`,
                    "warning"
                )
            } finally {
                setIsLoading(false)
            }

            return response
        }, [deviceId, deviceRef, formData, attachmentUUID])

    return {
        publishAmendment,
        isLoading,
        isLoaded,
        error,
    }
}

export default usePublishAmendment
